import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import { useTranslation } from 'react-i18next';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ReviewPage = () => {
  const [statistics, setStatistics] = useState({});
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchData = async () => {
    setLoading(true);
    try {
      const statisticsResponse = await axios.get(
        `${API_BASE_URL}/api/reviewAll.php?action=getInfoForReview`
      );
      setStatistics(statisticsResponse.data);

      const gradesUrl = new URL(
        `${API_BASE_URL}/api/reviewAll.php?action=getGradesWithStudentCounts`
      );

      const gradesResponse = await axios.get(gradesUrl.toString());
      if (gradesResponse.data?.grades) {
        setGrades(gradesResponse.data.grades);
      } else {
        setGrades([]);
      }
    } catch (error) {
      console.error('Error fetching data', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGradeClick = (classNumber) => {
    navigate(`/class/${classNumber}`);
  };

  const columns = [
    {
      field: 'class',
      headerName: t('review.grade'),
      width: 50,
      flex: 1,
      renderCell: (params) => (
        <Typography
          style={{
            textAlign: 'left',
            fontWeight: 'bold',
            color: '#1c75bc',
            cursor: 'pointer'
          }}
          onClick={() => handleGradeClick(params.value)}
        >
          {`${params.value}. ${t('review.grade_suffix')}`}
        </Typography>
      )
    },
    {
      field: 'student_count',
      headerName: t('review.student_count'),
      width: 100
    }
  ];

  return (
    <Box m="20px">
      {loading ? (
        <CircularProgress />
      ) : (
        <Box display="flex" gap="20px" alignItems="flex-start">
          {/* Tabela zauzima pola ekrana */}
          <Box sx={{ flex: '1', maxWidth: '55%', padding: '20px' }}>
            <DataGrid
              rows={grades}
              columns={columns}
              getRowId={(row) => row.class}
              autoHeight
              disablePagination
              sx={{
                '& .MuiDataGrid-root': {
                  backgroundColor: '#f9fafb',
                  borderRadius: '12px',
                  padding: '10px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #d1e3f3'
                },
                '& .MuiDataGrid-virtualScrollerRenderZone': {
                  backgroundColor: '#f9fafb'
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                  padding: '12px',
                  fontSize: '14px',
                  color: '#333'
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#3788d8',
                  color: '#ffffff',
                  borderBottom: 'none',
                  borderRadius: '12px 12px 0 0'
                },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: '#3788d8',
                  color: '#ffffff',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px'
                },
                '&:hover': {
                  boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.15)'
                }
              }}
            />
          </Box>

          <Box display="flex" flexDirection="column" gap="20px" flex="1">
            <Grid container spacing={2}>
              {[
                {
                  title: t('review.total_students'),
                  value: statistics.total_students || 0,
                  icon: <PeopleIcon sx={{ fontSize: 40, color: '#007bff' }} />
                },
                {
                  title: t('review.reviewed_students'),
                  value: statistics.reviewed_students || 0,
                  icon: (
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#28a745' }} />
                  )
                }
              ].map((stat, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    sx={{
                      backgroundColor: '#f0f4f7',
                      color: '#333',
                      padding: '20px',
                      borderRadius: '16px',
                      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        {stat.icon}
                        <Box ml={2}>
                          <Typography variant="h6" fontWeight="bold">
                            {stat.title}
                          </Typography>
                          <Typography variant="h4">{stat.value}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ReviewPage;
